import { z } from '@/utils/zod'

//
// pretax = Vorsteuer
// valueAdded = Umsatzsteuer
//
export const TaxTypeSchema = z.enum(['pre', 'vat'])

export type TaxType = z.infer<typeof TaxTypeSchema>

export const EditableBuiltInFields: (keyof Tax)[] = [
    'account',
    'pendingAccount',
    'discountAccount',
    'depositAccount',
    'reductionAccount',
] as const

export const TaxSchema = z
    .object({
        type: TaxTypeSchema,
        code: z.string().min(1).max(5),
        datevCode: z.number().int().min(1).max(99),
        rate: z.number().int().nonnegative().max(99),
        label: z.string().min(1).max(18),
        labelShort: z.string().min(1).max(8),
        // standard tax rate that comes from the base accounting plan will be true. Custom added ones will be false
        builtIn: z.boolean(),
        // default account number for taxes of this code
        account: z.number().int().min(1).max(999999999).nullable(),
        // discount account for VAT taxes
        discountAccount: z.number().int().min(1).max(999999999).nullable(),
        // reduction account for PRE taxes
        reductionAccount: z.number().int().min(1).max(999999999).nullable(),
        // deposit account for either VAT or PRE taxes
        depositAccount: z.number().int().min(1).max(999999999),
        // pending account for not-yet-due VAT taxes
        pendingAccount: z.number().int().min(1).max(999999999).nullable(),
    })
    .check(({ value, ...ctx }) => {
        if (!value.account && value.rate > 0) {
            ctx.issues.push({
                code: 'custom',
                input: value.account,
                params: {
                    i18n: { key: 'custom.required' },
                },
                path: ['account'],
            })
        } else if (value.rate === 0 && value.account) {
            ctx.issues.push({
                code: 'invalid_type',
                expected: 'null',
                received: 'number',
                input: value.rate,
                path: ['account'],
            })
        }

        if (value.type === 'vat') {
            if (!value.discountAccount) {
                ctx.issues.push({
                    code: 'custom',
                    input: value.discountAccount,
                    params: {
                        i18n: { key: 'custom.required' },
                    },
                    path: ['discountAccount'],
                })
            }

            if (value.rate > 0) {
                if (!value.pendingAccount) {
                    ctx.issues.push({
                        code: 'custom',
                        input: value.pendingAccount,
                        params: {
                            i18n: { key: 'custom.required' },
                        },
                        path: ['pendingAccount'],
                    })
                }
            }
        } else if (value.type === 'pre') {
            if (!value.reductionAccount) {
                ctx.issues.push({
                    code: 'custom',
                    input: value.reductionAccount,
                    params: {
                        i18n: { key: 'custom.required' },
                    },
                    path: ['reductionAccount'],
                })
            }

            if (value.pendingAccount) {
                ctx.issues.push({
                    code: 'invalid_type',
                    expected: 'null',
                    received: 'number',
                    input: value.pendingAccount,
                    path: ['pendingAccount'],
                })
            }
        }
    })

export type Tax = z.infer<typeof TaxSchema>
